import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import { PAYMENT_STATUS, getPaymentStatus } from '../../../constants/paymentStatus';
import TICKET_TYPE from '../../../constants/ticketTypes';
import { calcTotals } from '../utils/totalsCalculator';

/**
 * Create a new sales receipt/ticket
 * @param {Object} ticketData - Ticket data matching CreateTicketDto
 * @returns {Promise<Object>} Created ticket
 */
export const createTicket = async (ticketData) => {
  const response = await axiosInstance.post('Ticket/create-sale-ticket', ticketData);
  return response.data;
};

/**
 * Get all tickets
 * @returns {Promise<Array>} List of all tickets
 */
export const getAllTickets = async () => {
  const response = await axiosInstance.get('Ticket');
  return response.data;
};

/**
 * Get ticket by ID
 * @param {number} id - Ticket ID
 * @returns {Promise<Object>} Ticket details
 */
export const getTicketById = async (id) => {
  const response = await axiosInstance.get(`Ticket/${id}`);
  return response.data;
};

/**
 * Get ticket by ticket number
 * @param {string} ticketNumber - Ticket number (e.g., TKT-12345678-123)
 * @returns {Promise<Object>} Ticket details
 */
export const getTicketByNumber = async (ticketNumber) => {
  const response = await axiosInstance.get(`Ticket/by-number/${ticketNumber}`);
  return response.data;
};

/**
 * Get tickets by customer ID
 * @param {number} customerId - Customer ID
 * @returns {Promise<Array>} List of customer tickets
 */
export const getTicketsByCustomer = async (customerId) => {
  const response = await axiosInstance.get(`Ticket/by-customer/${customerId}`);
  return response.data;
};

/**
 * Get tickets by date range
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {Promise<Array>} List of tickets in date range
 */
export const getTicketsByDateRange = async (startDate, endDate) => {
  const response = await axiosInstance.get('Ticket/by-date-range', {
    params: { startDate, endDate }
  });
  return response.data;
};

/**
 * Get my sales (current user's sales)
 * @returns {Promise<Array>} List of current user's tickets
 */
export const getMySales = async () => {
  const response = await axiosInstance.get('Ticket/my-sales');
  return response.data;
};

/**
 * Get today's sales
 * @returns {Promise<Array>} List of today's tickets
 */
export const getTodaysSales = async () => {
  const response = await axiosInstance.get('Ticket/today');
  return response.data;
};

/**
 * Update ticket (refund, add notes, etc.)
 * @param {number} id - Ticket ID
 * @param {Object} updateData - Update data matching UpdateTicketDto
 * @returns {Promise<Object>} Updated ticket
 */
export const updateTicket = async (id, updateData) => {
  // ✅ Backend expects UpdateTicketDto with Id in the body at /Ticket/update-ticket endpoint
  const response = await axiosInstance.put('Ticket/update-ticket', {
    ...updateData,
    Id: id
  });
  return response.data;
};

/**
 * Delete ticket
 * @param {number} id - Ticket ID
 * @returns {Promise<void>}
 */
export const deleteTicket = async (id) => {
  await axiosInstance.delete(`Ticket/${id}`);
};

/**
 * Get sales statistics
 * @param {Date|string} startDate - Optional start date
 * @param {Date|string} endDate - Optional end date
 * @returns {Promise<Object>} Sales statistics
 */
export const getStatistics = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  
  const response = await axiosInstance.get('Ticket/statistics', { params });
  return response.data;
};

/**
 * Record payment for a ticket
 * @param {Object} paymentData - Payment data { ticketId, paymentMethodId, amount, paymentDate }
 * @returns {Promise<Object>} Updated ticket with payment recorded
 */
export const recordTicketPayment = async (paymentData) => {
  const response = await axiosInstance.post('Ticket/record-payment', paymentData);
  return response.data;
};

/**
 * Transform cart data to CreateTicketDto format
 * @param {Object} params - Parameters for ticket creation
 * @returns {Object} Formatted ticket data
 */
export const transformCartToTicketDto = ({
  cart,
  customer,
  walkInCustomerName = '',
  customerPhone = '',
  selectedPaymentMethod,
  paidAmount,
  discount,
  customVATRate,
  receiptNumber,
  notes = '',
  useMultiplePayments = false, // ✅ Multi-payment flag
  paymentMethods = [], // ✅ Array of payment methods for multi-payment (split payments)
  allPaymentMethods = [], // ✅ Full list of available payment methods (to find MULTIPLE)
  ticketType = TICKET_TYPE.SALE, // ✅ Ticket type: "SaleTicket" or "RepairTicket" (string)
  pickupDate = null, // ✅ Pickup date for repair tickets
  preCalculatedTotals = null, // ✅ Pre-calculated totals from dialog (to avoid recalculation)
  isTvaIncluded = true // ✅ Whether prices include VAT
}) => {
  // ✅ Use pre-calculated totals from dialog if provided, otherwise calculate
  let subtotal, discountAmount, vatAmount, totalAmountInclVATAfterDiscount;
  
  if (preCalculatedTotals) {
    // ✅ Use totals already calculated in the dialog
    subtotal = preCalculatedTotals.subtotal;
    discountAmount = preCalculatedTotals.discountAmount || 0;
    vatAmount = preCalculatedTotals.totalVAT;
    totalAmountInclVATAfterDiscount = preCalculatedTotals.totalIncl;
  } else {
    // ✅ Fallback: Calculate if not provided (backward compatibility)
    subtotal = cart.reduce((sum, item) => sum + item.Quantity * item.Price, 0);
    discountAmount = discount?.amount || 0;
    const subtotalAfterDiscount = subtotal - discountAmount;
    const vatPercentage = customVATRate !== null && customVATRate !== undefined ? customVATRate : 21;
    vatAmount = (subtotalAfterDiscount * vatPercentage) / 100;
    totalAmountInclVATAfterDiscount = Math.round((subtotalAfterDiscount + vatAmount) * 100) / 100;
  }

  // Determine VAT percentage (use custom VAT rate or default from items)
  const vatPercentage = customVATRate !== null && customVATRate !== undefined 
    ? customVATRate 
    : 21;

  // Calculate discount details
  let discountType = null;
  let discountPercentage = null;
  let discountReason = null;

  if (discount) {
    if (discount.type === 'percentage') {
      discountType = 'Percentage';
      discountPercentage = discount.value;
      if (!preCalculatedTotals) {
        discountAmount = (subtotal * discount.value) / 100;
      }
    } else {
      discountType = 'Fixed';
      if (!preCalculatedTotals) {
        discountAmount = discount.value;
      }
    }
    discountReason = discount.reason || null;
  }
  
  // ✅ Calculate totalAmountInclVAT using PriceInclVAT when available to avoid rounding errors
  // When VAT is included, PriceInclVAT preserves the original entered price
  const totalAmountInclVAT = cart.reduce((sum, item) => {
    const itemPriceIncl = item.PriceInclVAT || (item.Price * (1 + vatPercentage / 100));
    return sum + item.Quantity * itemPriceIncl;
  }, 0);

  // ✅ Calculate paid amount - handle both single and multi-payment
  let paid = 0;
  if (useMultiplePayments && paymentMethods && paymentMethods.length > 0) {
    // Multi-payment: sum all payment method amounts
    paid = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
  } else {
    // Single payment: use paidAmount if provided
    // ✅ null = no payment (repair tickets), empty string/undefined = default to full total (sale tickets)
    if (paidAmount === null) {
      // Explicitly null - no payment (repair tickets)
      paid = 0;
    } else {
      const parsedPaid = parseFloat(paidAmount);
      paid = (!isNaN(parsedPaid) && parsedPaid >= 0) ? parsedPaid : totalAmountInclVATAfterDiscount;
    }
  }

  // Calculate change
  const amountInChange = Math.max(0, paid - totalAmountInclVATAfterDiscount);

  // Transform line items
  const lineItems = cart.map(item => {
    // ✅ Use custom VAT rate if set, otherwise use item's VAT rate
    const itemVAT = customVATRate !== null && customVATRate !== undefined 
      ? customVATRate 
      : (item.VAT || 0);
    const quantity = parseFloat(item.Quantity) || 1;
    const unitPriceExclTax = parseFloat(item.Price) || 0;
    
    // ✅ Use PriceInclVAT from cart when available to preserve original entered price
    const unitPriceInclVat = item.PriceInclVAT 
      ? parseFloat(item.PriceInclVAT)
      : unitPriceExclTax * (1 + itemVAT / 100);

    // Calculate line item totals BEFORE discount
    const lineSubtotal = quantity * unitPriceExclTax;
    const lineTotalInclVAT = quantity * unitPriceInclVat;

    // Calculate item discount proportionally (same logic as CartPanel)
    let itemDiscountPercentage = 0;
    let itemDiscountAmount = 0;
    let lineDiscountAmount = 0;
    
    if (discount && discount.type === 'percentage') {
      itemDiscountPercentage = discount.value;
      lineDiscountAmount = (lineSubtotal * itemDiscountPercentage) / 100;
    } else if (discount && discount.type === 'fixed') {
      // For fixed discount, distribute proportionally based on subtotal
      const itemRatio = subtotal > 0 ? lineSubtotal / subtotal : 0;
      lineDiscountAmount = discountAmount * itemRatio;
    }
    
    // Calculate totals AFTER discount
    const lineSubtotalAfterDiscount = lineSubtotal - lineDiscountAmount;
    const lineVATAmount = (lineSubtotalAfterDiscount * itemVAT) / 100;
    const lineTotalInclVATAfterDiscount = lineSubtotalAfterDiscount + lineVATAmount;

    return {
      ProductId: item.Id || null,
      ServiceName: item.ServiceName || null,
      ServiceDescription: item.Description || item.ServiceDescription || null,
      ProductSerial: item.SerialNumber || item.ProductSerial || null,
      Quantity: quantity,
      UnitPriceExclTax: Math.round(unitPriceExclTax * 100) / 100,
      UnitPriceInclVat: Math.round(unitPriceInclVat * 100) / 100,
      VATRate: itemVAT,
      VATPercentage: itemVAT, // ✅ Backend expects VATPercentage
      CostPrice: parseFloat(item.CostPrice || item.BuyPrice || 0) || null,
      Discount: itemDiscountPercentage,
      DiscountAmount: Math.round(lineDiscountAmount * 100) / 100,
      VATAmount: Math.round(lineVATAmount * 100) / 100,
      Subtotal: Math.round(lineSubtotal * 100) / 100,
      TotalAmountInclVATAfterDiscount: Math.round(lineTotalInclVATAfterDiscount * 100) / 100,
      TotalAmountInclVAT: Math.round(lineTotalInclVAT * 100) / 100,
      UpdatePrice: item.UpdatePrice === true
    };
  });

  // PaidAmount sent to backend is capped at the actual total due — any excess is change, not income.
  // ✅ For multi-payment, allow exact match even if it equals total (common case)
  const actualPaidAmount = paid >= 0
    ? Math.round(Math.min(Math.max(paid, 0), totalAmountInclVATAfterDiscount) * 100) / 100
    : null;


  // ✅ Build SplitPayments array for multi-payment (same format as invoices)
  const splitPayments = useMultiplePayments && paymentMethods && paymentMethods.length > 0 
    ? paymentMethods.map(pm => ({
        PaymentMethodId: parseInt(pm.method) || parseInt(pm.id) || 1, // method contains the payment method ID
        Amount: parseFloat(pm.amount) || 0
      }))
    : null;

  // ✅ Calculate payment status based on paid amount and total
  const paymentStatus = getPaymentStatus(actualPaidAmount || 0, totalAmountInclVATAfterDiscount);

  // ✅ Determine which payment method to use
  let finalPaymentMethodId = null;
  let finalPaymentMethodName = null;

  if (actualPaidAmount && actualPaidAmount > 0) {
    if (useMultiplePayments && allPaymentMethods && allPaymentMethods.length > 0) {
      // Find the MULTIPLE payment method from the full list
      const multipleMethod = allPaymentMethods.find(
        pm => pm.name?.toUpperCase() === 'MULTIPLE' || 
              pm.Name?.toUpperCase() === 'MULTIPLE' ||
              pm.label?.toUpperCase() === 'MULTIPLE'
      );
      
      if (multipleMethod) {
        finalPaymentMethodId = parseInt(multipleMethod.id || multipleMethod.Id) || null;
        finalPaymentMethodName = multipleMethod.name || multipleMethod.Name || multipleMethod.label || 'MULTIPLE';
      } else {

      }
    } else if (selectedPaymentMethod) {
      // Single payment method
      finalPaymentMethodId = parseInt(selectedPaymentMethod?.Id || selectedPaymentMethod?.id) || null;
      finalPaymentMethodName = selectedPaymentMethod?.Name || selectedPaymentMethod?.name || selectedPaymentMethod?.label || null;
    }
  }

  const ticketDto = {
    TicketNumber: receiptNumber, // ✅ Always include ticket number (can be null, backend will generate)
    TicketType: ticketType, // ✅ "SaleTicket" or "RepairTicket" (string)
    CustomerId: customer?.Id || null,
    CustomerName: customer?.Name || walkInCustomerName || 'Walk-in Customer',
    CustomerPhone: customerPhone || customer?.PhoneNumber || customer?.Phone || null,
    PickupDate: pickupDate || null, // ✅ Only for RepairTicket
    // ✅ Use determined payment method
    PaymentMethodId: finalPaymentMethodId,
    PaymentMethod: finalPaymentMethodName,
    Subtotal: Math.round(subtotal * 100) / 100,
    VATPercentage: vatPercentage,
    VATAmount: Math.round(vatAmount * 100) / 100,
    DiscountType: discountType,
    DiscountPercentage: discountPercentage,
    DiscountAmount: Math.round(discountAmount * 100) / 100,
    DiscountReason: discountReason,
    TotalAmountInclVATAfterDiscount: Math.round(totalAmountInclVATAfterDiscount * 100) / 100,
    TotalAmountInclVAT: Math.round(totalAmountInclVAT * 100) / 100,
    PaidAmount: actualPaidAmount,
    // ✅ AmountInChange removed - not sent to backend (only used in UI)
    Notes: notes || (discountReason ? `Discount: ${discountReason}` : ''),
    LineItems: lineItems,
    // ✅ Add SplitPayments for multi-payment support (same as invoices)
    SplitPayments: splitPayments,
    // ✅ Add payment status (Paid, Not Paid, Partial Paid)
    Status: paymentStatus
  };

  // ✅ Return DTO + amountInChange for UI display only (not sent to backend)
  return {
    dto: ticketDto,
    amountInChange: Math.round(amountInChange * 100) / 100 // For UI display only
  };
};

/**
 * Create ticket from cart and print
 * @param {Object} params - All required parameters for ticket creation
 * @returns {Promise<Object>} Created ticket with print status
 */
export const createAndPrintTicket = async (params) => {
  try {
    // Transform cart to ticket DTO
    const { dto: ticketDto, amountInChange } = transformCartToTicketDto(params);
    
    // Create ticket in database
    const createdTicket = await createTicket(ticketDto);
    
    return {
      success: true,
      ticket: createdTicket,
      amountInChange, // For UI display
      message: 'Ticket created successfully'
    };
  } catch (error) {

    return {
      success: false,
      error: error.message || 'Failed to create ticket',
      message: 'Failed to create ticket in database'
    };
  }
};

/**
 * Validate ticket data before submission
 * @param {Object} ticketData - Ticket data to validate
 * @returns {Object} Validation result
 */
export const validateTicketData = (ticketData) => {
  const errors = [];

  // ✅ Only validate payment method if there's a paid amount > 0
  if (ticketData.PaidAmount && ticketData.PaidAmount > 0) {
    // ✅ Validate PaymentMethodId is present when payment is made
    if (!ticketData.PaymentMethodId || typeof ticketData.PaymentMethodId !== 'number') {
      errors.push('Payment method ID is required when payment is made');
    }

    // ✅ Validate PaymentMethod string exists (for backward compatibility)
    if (!ticketData.PaymentMethod || typeof ticketData.PaymentMethod !== 'string' || ticketData.PaymentMethod.trim() === '') {
      errors.push('Payment method name is required when payment is made');
    }
  }

  // ✅ Validate PaidAmount - allow null for unpaid tickets
  if (ticketData.PaidAmount !== null && ticketData.PaidAmount !== undefined && ticketData.PaidAmount < 0) {
    errors.push('Paid amount cannot be negative');
  }

  if (!ticketData.LineItems || ticketData.LineItems.length === 0) {
    errors.push('At least one product is required');
  }

  if (ticketData.LineItems) {
    ticketData.LineItems.forEach((item, index) => {
      // ✅ Either ProductId OR ServiceName is required
      if (!item.ProductId && !item.ServiceName) {
        errors.push(`Line item ${index + 1}: Product ID or Service Name is required`);
      }
      if (!item.Quantity || item.Quantity <= 0) {
        errors.push(`Line item ${index + 1}: Quantity must be greater than 0`);
      }
    });
  }

  // ✅ Validate PickupDate for RepairTicket
  if (ticketData.TicketType === TICKET_TYPE.REPAIR && !ticketData.PickupDate) {
    errors.push('Pickup date is required for repair tickets');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Export all functions
export default {
  createTicket,
  getAllTickets,
  getTicketById,
  getTicketByNumber,
  getTicketsByCustomer,
  getTicketsByDateRange,
  getMySales,
  getTodaysSales,
  updateTicket,
  deleteTicket,
  getStatistics,
  transformCartToTicketDto,
  createAndPrintTicket,
  validateTicketData
};